import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paidVoucherAPI } from '../../services/api';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './PaidVoucherList.css';

const PaidVoucherList = () => {
  const { t } = useTranslation();
  const [paidVouchers, setPaidVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  useEffect(() => {
    loadPaidVouchers();
  }, [page]);

  const loadPaidVouchers = async () => {
    try {
      setLoading(true);
      const response = await paidVoucherAPI.getAll(searchTerm, page, pageSize);
      setPaidVouchers(response.data.items || []);
      const totalCount = response.data.totalCount || 0;
      setTotalPages(Math.ceil(totalCount / pageSize) || 1);
    } catch (err) {
      setErrorMessage(t('paidVoucher.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadPaidVouchers();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await paidVoucherAPI.delete(id);
        setSuccessMessage(t('paidVoucher.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadPaidVouchers();
      } catch (err) {
        setErrorMessage(t('paidVoucher.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('paidVoucher.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('paidVoucher.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await paidVoucherAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('paidVoucher.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadPaidVouchers();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('paidVoucher.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('paidVoucher.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paidVouchers.map(rv => rv.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await paidVoucherAPI.exportExcel();
          filename = `PaidVouchers_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await paidVoucherAPI.exportCsv();
          filename = `PaidVouchers_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await paidVoucherAPI.exportPdf();
          filename = `PaidVouchers_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('paidVoucher.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('paidVoucher.list.messages.exportError', { format: format.toUpperCase() }) + ` ${err.response?.data?.message || err.message}`);
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
          <div className="page-actions-left">
            <Link to="/paid-voucher/create" className="btn-add">
              + {t('paidVoucher.list.addNew')}
            </Link>
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('paidVoucher.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              <button className="search-icon" onClick={handleSearch}>
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
          <div className="action-buttons">
            <div className="export-menu-container">
              <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
                <i className="fas fa-file-export"></i>
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('excel')}>
                    <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                  </button>
                  <button onClick={() => handleExport('csv')}>
                    <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                  </button>
                  <button onClick={() => handleExport('pdf')}>
                    <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                  </button>
                </div>
              )}
            </div>
            <button className="action-btn action-btn-delete" title={t('paidVoucher.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />

        <SuccessModal
          show={showSuccessModal}
          message={successMessage}
          onClose={() => setShowSuccessModal(false)}
        />

        <ConfirmModal
          show={showConfirmModal}
          title={confirmData?.title}
          message={confirmData?.message}
          confirmText={confirmData?.confirmText}
          cancelText={confirmData?.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === paidVouchers.length && paidVouchers.length > 0}
                  />
                </th>
                <th>{t('paidVoucher.list.columns.id')}</th>
                <th>{t('paidVoucher.list.columns.voucherNumber')}</th>
                <th>{t('paidVoucher.list.columns.date')}</th>
                <th>{t('paidVoucher.list.columns.paidTo')}</th>
                <th>{t('paidVoucher.list.columns.amount')}</th>
                <th>{t('paidVoucher.list.columns.paymentMethod')}</th>
                <th>{t('paidVoucher.list.columns.chequeNumber')}</th>
                <th>{t('paidVoucher.list.columns.bank')}</th>
                <th>{t('paidVoucher.list.columns.purpose')}</th>
                <th>{t('paidVoucher.list.columns.status')}</th>
                <th>{t('paidVoucher.list.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paidVouchers.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    {t('paidVoucher.list.messages.noData')}
                  </td>
                </tr>
              ) : (
                paidVouchers.map((voucher) => (
                  <tr key={voucher.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(voucher.id)}
                        onChange={() => handleSelectOne(voucher.id)}
                      />
                    </td>
                    <td>{voucher.id}</td>
                    <td>{voucher.voucherNumber}</td>
                    <td>{voucher.voucherDate ? new Date(voucher.voucherDate).toLocaleDateString() : '-'}</td>
                    <td>{voucher.paidTo || '-'}</td>
                    <td>{voucher.amount?.toFixed(2) || '0.00'}</td>
                    <td>{voucher.paymentMethod || '-'}</td>
                    <td>{voucher.chequeNumber || '-'}</td>
                    <td>{voucher.bank || '-'}</td>
                    <td>{voucher.purpose || '-'}</td>
                    <td>
                      <span className={voucher.isActive ? 'status-yes-text' : 'status-no-text'}>
                        {voucher.isActive ? t('common.yes') : t('common.no')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-cell">
                        <Link
                          to={`/paid-voucher/view/${voucher.id}`}
                          className="btn btn-sm btn-view"
                          title={t('common.view')}
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                        <Link
                          to={`/paid-voucher/edit/${voucher.id}`}
                          className="btn btn-sm btn-edit"
                          title={t('common.edit')}
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          className="btn btn-sm btn-delete"
                          onClick={() => handleDelete(voucher.id)}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous') || 'Previous'}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaidVoucherList;

